import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, Loader, CheckCircle, Eye, EyeOff } from 'lucide-react'
import toast from 'react-hot-toast'
import AuthHeader from '../../components/auth/AuthHeader'
import { vendorService } from '../../services/authService'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [otpsId, setOtpsId] = useState(null)
  const [emailOtp, setEmailOtp] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const emailOtpRef = useRef(null)
  const phoneOtpRef = useRef(null)
  const newPasswordRef = useRef(null)

  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneOtp, setPhoneOtp] = useState('')
  const [phoneOtpSent, setPhoneOtpSent] = useState(false)
  const [isPhoneVerifying, setIsPhoneVerifying] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(false)

  const getCurrentStep = () => {
    // 1: enter email, 2: email OTP, 3: phone OTP (if applicable), 4: reset password
    if (!otpSent) return 1
    if (otpSent && !phoneOtpSent && !isVerified) return 2
    if (phoneOtpSent && !isVerified) return 3
    if (isVerified) return 4
    return 2
  }

  useEffect(() => {
    const step = getCurrentStep()
    if (step === 2 && emailOtpRef.current) {
      emailOtpRef.current.focus()
    }
    if (step === 3 && phoneOtpRef.current) {
      phoneOtpRef.current.focus()
    }
    if (step === 4 && newPasswordRef.current) {
      newPasswordRef.current.focus()
    }
  }, [otpSent, phoneOtpSent, isVerified])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      // Call vendor reset-password API
      const response = await vendorService.forgotPassword(email)

      // Store returned data (otps_id/email etc.) so next step (OTP) can use it
      try {
        if (response && response.data) {
          sessionStorage.setItem('passwordReset', JSON.stringify(response.data))
          // set otp id and show OTP input
          if (response.data.otps_id) setOtpsId(response.data.otps_id)
        }
      } catch (err) {
        // ignore sessionStorage errors
      }

      setOtpSent(true)
      toast.success(response?.message || 'OTP sent to your email!')
    } catch (error) {
      toast.error(error.message || 'Failed to send reset link. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Inline OTP flow will be shown in the same card after sending OTP

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
      <AuthHeader />
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full space-y-8"
      >
        {/* Stepper */}
        <div className="flex items-center justify-center mb-2">
          {(() => {
            const steps = ['Email', 'Email OTP', 'Phone OTP', 'Reset']
            const current = getCurrentStep()
            return (
              <div className="flex items-center gap-4">
                {steps.map((label, idx) => {
                  const stepIndex = idx + 1
                  const active = stepIndex === current
                  const done = stepIndex < current
                  return (
                    <div key={label} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-none shrink-0 overflow-hidden ${done ? 'bg-success-500 text-white' : active ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-600'}`}>
                        {done ? '✓' : stepIndex}
                      </div>
                      <div className="ml-2 text-sm text-secondary-600">{label}</div>
                      {idx !== steps.length - 1 && <div className={`w-8 h-[2px] mx-3 ${done ? 'bg-success-500' : 'bg-secondary-200'}`}></div>}
                    </div>
                  )
                })}
              </div>
            )
          })()}
        </div>
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-secondary-900 dark:text-secondary-100">Forgot your password?</h2>
          <p className="mt-2 text-secondary-600">
            No worries! Enter your email address and we'll send you an OTP to reset your password.
          </p>
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="card p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="label block mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`input pl-10 ${isVerified ? 'bg-secondary-100 dark:bg-secondary-800 cursor-not-allowed' : ''}`}
                  disabled={isVerified}
                  readOnly={isVerified}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            {/* If OTP is not yet sent, show Send OTP button */}
            {!otpSent && (
              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary btn-lg w-full group py-3 mt-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Sending OTP...
                  </>
                ) : (
                  <>
                    Send OTP
                  </>
                )}
              </button>
            )}

            {/* After OTP is sent, show OTP input & verify button inline */}
            {otpSent && !isVerified && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="otp" className="label block mb-2">Enter OTP</label>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    value={emailOtp}
                    onChange={(e) => setEmailOtp(e.target.value)}
                    ref={emailOtpRef}
                    className="input"
                    placeholder="Enter the 6-digit OTP"
                    maxLength={6}
                  />
                </div>

                <div className="flex gap-3">
                  {!emailVerified ? (
                    <>
                      <button
                    type="button"
                    onClick={async () => {
                      // Verify OTP
                      if (!emailOtp || emailOtp.length < 4) {
                        toast.error('Please enter a valid OTP')
                        return
                      }
                      setIsVerifying(true)
                      try {
                        const verifyResponse = await vendorService.verifyForgotPasswordOtp({
                          email: email,
                          otp: emailOtp,
                          otps_id: otpsId,
                        })

                        if (!verifyResponse.success) {
                          toast.error(verifyResponse.message || 'OTP verification failed')
                          return
                        }

                        // If API returns a token for resetting password, navigate to reset page
                        const returnedToken = verifyResponse.data?.token || verifyResponse.data?.reset_token
                        if (returnedToken) {
                          // navigate to reset password page with token
                          window.location.href = `/auth/reset-password?token=${returnedToken}`
                          return
                        }

                        // If backend returned a phone number for further verification, show phone OTP input
                        const returnedPhone = verifyResponse.data?.user_phone || verifyResponse.data?.phone || verifyResponse.data?.userPhone
                        if (returnedPhone) {
                          setEmailVerified(true)
                          setPhoneNumber(returnedPhone)
                          setPhoneOtpSent(true)
                          // ensure otpsId is set for phone verification as well
                          if (verifyResponse.data?.otps_id) setOtpsId(verifyResponse.data.otps_id)
                          toast.success('OTP sent to ' + returnedPhone)
                          return
                        }

                        // Otherwise mark as verified and show inline reset form
                        setEmailVerified(true)
                        setIsVerified(true)
                        toast.success(verifyResponse.message || 'OTP verified successfully')
                      } catch (err) {
                        toast.error(err.message || 'Failed to verify OTP')
                      } finally {
                        setIsVerifying(false)
                      }
                    }}
                    className="btn btn-primary py-2.5 px-4"
                    disabled={isVerifying}
                  >
                    {isVerifying ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      'Verify OTP'
                    )}
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          // Resend OTP
                          setIsLoading(true)
                          try {
                            const resp = await vendorService.forgotPassword(email)
                            if (resp.success) {
                              setOtpsId(resp.data?.otps_id || otpsId)
                              toast.success(resp.message || 'OTP resent')
                            } else {
                              toast.error(resp.message || 'Failed to resend OTP')
                            }
                          } catch (err) {
                            toast.error(err.message || 'Failed to resend OTP')
                          } finally {
                            setIsLoading(false)
                          }
                        }}
                        className="btn btn-secondary py-2.5 px-4"
                      >
                        Resend OTP
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center text-success-600">
                      <CheckCircle className="w-6 h-6" />
                      <span className="ml-2 font-medium">Verified</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Phone OTP step shown when returned by email verification */}
            {phoneOtpSent && !isVerified && (
              <div className="space-y-4">
                <p className="text-sm text-secondary-600">OTP sent to <strong>{phoneNumber}</strong></p>
                <div>
                  <label htmlFor="phoneOtp" className="label block mb-2">Enter phone OTP</label>
                  <input
                    id="phoneOtp"
                    name="phoneOtp"
                    type="text"
                    inputMode="numeric"
                    value={phoneOtp}
                    onChange={(e) => setPhoneOtp(e.target.value)}
                    ref={phoneOtpRef}
                    className="input"
                    placeholder="Enter the 6-digit OTP"
                    maxLength={6}
                  />
                </div>

                <div className="flex gap-3">
                  {!phoneVerified ? (
                    <>
                      <button
                    type="button"
                    onClick={async () => {
                      if (!phoneOtp || phoneOtp.length < 4) {
                        toast.error('Please enter a valid phone OTP')
                        return
                      }
                      setIsPhoneVerifying(true)
                      try {
                        const phoneVerifyResp = await vendorService.verifyForgotPasswordPhoneOtp({
                          phone: phoneNumber,
                          otp: phoneOtp,
                          otps_id: otpsId,
                        })

                        if (!phoneVerifyResp.success) {
                          toast.error(phoneVerifyResp.message || 'Phone OTP verification failed')
                          return
                        }

                        // If phone verify returns a token, navigate to reset page
                        const returnedToken = phoneVerifyResp.data?.token || phoneVerifyResp.data?.reset_token
                        if (returnedToken) {
                          window.location.href = `/auth/reset-password?token=${returnedToken}`
                          return
                        }

                        // Otherwise proceed to inline reset form
                        setPhoneVerified(true)
                        setIsVerified(true)
                        toast.success(phoneVerifyResp.message || 'Phone OTP verified')
                      } catch (err) {
                        toast.error(err.message || 'Failed to verify phone OTP')
                      } finally {
                        setIsPhoneVerifying(false)
                      }
                    }}
                    className="btn btn-primary py-2.5 px-4"
                    disabled={isPhoneVerifying}
                  >
                    {isPhoneVerifying ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      'Verify phone OTP'
                    )}
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          // Resend OTP (fallback to forgotPassword which triggers OTP again)
                          setIsLoading(true)
                          try {
                            const resp = await vendorService.forgotPassword(email)
                            if (resp.success) {
                              setOtpsId(resp.data?.otps_id || otpsId)
                              toast.success(resp.message || 'OTP resent')
                            } else {
                              toast.error(resp.message || 'Failed to resend OTP')
                            }
                          } catch (err) {
                            toast.error(err.message || 'Failed to resend OTP')
                          } finally {
                            setIsLoading(false)
                          }
                        }}
                        className="btn btn-secondary py-2.5 px-4"
                      >
                        Resend OTP
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center text-success-600">
                      <CheckCircle className="w-6 h-6" />
                      <span className="ml-2 font-medium">Verified</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* After verification, allow inline password reset */}
            {isVerified && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="newPassword" className="label block mb-2">New password</label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      ref={newPasswordRef}
                      className="input pr-10"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="label block mb-2">Confirm password</label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input pr-10"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Inline validation */}
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="text-error-500 text-sm mt-1">Passwords do not match</p>
                  )}
                  {newPassword && newPassword.length > 0 && newPassword.length < 8 && (
                    <p className="text-error-500 text-sm mt-1">Password must be at least 8 characters</p>
                  )}
                </div>
                <div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (newPassword !== confirmPassword) {
                        toast.error('Passwords do not match')
                        return
                      }
                      if (newPassword.length < 8) {
                        toast.error('Password must be at least 8 characters')
                        return
                      }
                      setIsLoading(true)
                      try {
                        const payload = {
                          email,
                          password: newPassword,
                          confirm_password: confirmPassword,
                          otps_id: otpsId,
                        }
                        const resp = await vendorService.resetPassword(payload)
                        if (resp.success) {
                          toast.success(resp.message || 'Password reset successfully')
                          window.location.href = '/auth/login'
                        } else {
                          toast.error(resp.message || 'Failed to reset password')
                        }
                      } catch (err) {
                        toast.error(err.message || 'Failed to reset password')
                      } finally {
                        setIsLoading(false)
                      }
                    }}
                    className="btn btn-primary w-full py-3 mt-2"
                    disabled={isLoading || newPassword !== confirmPassword || newPassword.length < 8}
                  >
                    {isLoading ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin mr-2" />
                        Resetting...
                      </>
                    ) : (
                      'Reset password'
                    )}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* removed duplicate Back to login link per design */}
        </motion.div>

        {/* Help Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center"
        >
          <p className="text-sm text-secondary-600">
            Remember your password?{' '}
            <Link to="/auth/login" className="text-primary-600 hover:text-primary-500 font-medium">
              Sign in
            </Link>
          </p>
        </motion.div>
      </motion.div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
